<template>
  <div>
    <div class="form-group form-group-border-bottom d-flex flex-row padding-10 align-items-center position-relative">
      <label v-if="label" class="d-flex flex-row-reverse align-items-center">
        <span v-if="field.required" class="required">*</span>
        <SvgImage v-if="field.icon" :src="field.icon" class="label-icon" />
      </label>
      <div class="select-wrapper flex-grow-1 position-relative">
        <!-- Hidden input for form submission -->
        <input 
          type="hidden" 
          :name="name" 
          :id="name" 
          :value="selectedValue || ''"
          :required="field.required"
        />
        
        <!-- Text element - shown when select is closed -->
        <button
          v-if="!isOpen"
          type="button"
          class="select-text-display select-trigger-btn"
          @click.stop="openSelect"
          :aria-expanded="false"
          aria-haspopup="listbox"
          :aria-invalid="fieldAriaInvalid"
          :aria-describedby="fieldDescribedBy"
          :aria-required="fieldAriaRequired"
        >
          <span class="select-text">{{ displayText }}</span>
          <CaretComponent :open="false" />
        </button>
        
        <!-- Custom dropdown - shown when opened -->
        <div v-if="isOpen" class="select-control-wrapper">
          <button type="button" class="select-display-clickable select-trigger-btn" @click.stop="toggleDropdown" :aria-expanded="true" aria-haspopup="listbox" :aria-invalid="fieldAriaInvalid" :aria-describedby="fieldDescribedBy" :aria-required="fieldAriaRequired">
            <span class="select-text">{{ displayText }}</span>
            <CaretComponent :open="true" />
          </button>
          <div v-if="isOpen" class="select-dropdown">
            <button
              type="button"
              v-for="(option, idx) in options"
              :key="idx"
              class="select-option"
              :class="{ 'selected': optionValue(option) == selectedValue }"
              @click.stop="selectOption(option)"
            >
              {{ optionText(option) }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="invalid-feedback" :id="`${name}invalid`">{{field.error_message}}</div>
  </div>
</template>
<script>
import CaretComponent from "@/components/CaretComponent";
import SvgImage from "@/components/generic/SvgImage";
import formFieldA11y from '@/mixins/formFieldA11y.js'

export default {
  mixins: [formFieldA11y],
  components: {
    CaretComponent,
    SvgImage
  },
  props: {
    label: String,
    type: String,
    name: String,
    selected: String,
    options: Array,
    simple: {
      type: Boolean,
      default: false
    },
    field: Object
  },
  computed: {
    displayText() {
      // If user has selected something, show the selected text
      if (this.selectedValue !== null && this.selectedValue !== '' && this.selectedText !== '') {
        return this.selectedText;
      }
      // Otherwise, show the label
      return this.label || '';
    }
  },
  data() {
    return {
      isOpen: false,
      selectedValue: null,
      selectedText: ''
    }
  },
  created() {
    if (this.field && this.field.value !== null && this.field.value !== undefined && this.field.value !== '') {
      this.selectedValue = this.field.value;
      this.updateSelectedText();
    } else {
      // No value provided, start with no selection - show label
      this.selectedValue = null;
      this.selectedText = '';
    }
  },
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside);
    // Focus select when it opens
    this.$nextTick(() => {
      if (this.isOpen && this.$refs.selectElement) {
        this.$refs.selectElement.focus();
      }
      // Ensure hidden input is updated with the selected value
      if (this.selectedValue !== null && this.selectedValue !== '') {
        const hiddenInput = this.$el.querySelector(`input[type="hidden"][name="${this.name}"]`);
        if (hiddenInput) {
          hiddenInput.value = this.selectedValue;
        }
      }
    });
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    openSelect(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isOpen = true;
    },
    toggleDropdown(event) {
      event.preventDefault();
      event.stopPropagation();
      // Dropdown is already open, clicking again will close it via handleClickOutside
    },
    closeSelect() {
      // If no selection was made, keep showing label
      if (this.selectedValue === null || this.selectedValue === '') {
        this.selectedText = '';
      }
      this.isOpen = false;
    },
    selectOption(option) {
      this.selectedValue = this.optionValue(option);
      this.selectedText = this.optionText(option);
      this.isOpen = false;
      // Update hidden input
      const hiddenInput = this.$el.querySelector(`input[type="hidden"][name="${this.name}"]`);
      if (hiddenInput) {
        hiddenInput.value = this.selectedValue;
      }
    },
    optionValue(option) {
      if (this.simple) {
        return option;
      }
      return option.val;
    },
    optionText(option) {
      if (this.simple) {
        return option;
      }
      return option.text;
    },
    updateSelectedText() {
      if (this.selectedValue === null || this.selectedValue === '') {
        this.selectedText = '';
        return;
      }
      const option = this.options.find(opt => this.optionValue(opt) == this.selectedValue);
      if (option) {
        this.selectedText = this.optionText(option);
      }
    },
    handleClickOutside(event) {
      if (this.isOpen && !this.$el.contains(event.target)) {
        this.closeSelect();
      }
    }
  }
}
</script>
<style scoped>
.select-wrapper {
  position: relative;
}

.select-text-display {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.05rem 0.5rem 0.15rem 0.5rem;
  -moz-padding-start: calc(0.5rem - 3px);
  min-height: 28px;
  max-height: 28px;
  height: 28px;
  box-sizing: border-box;
}

.select-trigger-btn {
  width: 100%;
  text-align: inherit;
  border: none;
  background: transparent;
  font: inherit;
  color: #EF3D49;
}

.select-text {
  color: inherit;
  flex-grow: 1;
}

.select-control-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
}

.select-display-clickable {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.05rem 0.5rem 0.15rem 0.5rem;
  -moz-padding-start: calc(0.5rem - 3px);
  min-height: 28px;
  max-height: 28px;
  height: 28px;
  box-sizing: border-box;
  width: 100%;
}

.select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
}

.select-option {
  display: block;
  width: 100%;
  text-align: inherit;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  background: transparent;
  font: inherit;
  color: inherit;
}

.select-option:last-child {
  border-bottom: none;
}

.select-option:hover {
  background-color: #f5f5f5;
}

.select-option.selected {
  background-color: #e8f4f8;
  color: #ef3d49;
}

.required {
  color: red;
  margin-left: 5px;
}

.label-icon {
  margin-left: 8px;
  height: 20px;
  width: auto;
}
.form-group {
  margin-top: 10px !important;
}
</style>

